import React, { useState, useEffect } from 'react';
import './TooltipManager.scss';

interface TooltipState {
  visible: boolean;
  text: string;
  x: number;
  y: number;
  position?: 'top' | 'bottom';
  arrowOffset?: number;
}

let setTooltip: ((tooltip: TooltipState) => void) | null = null;

export const showTooltip = (text: string, target: HTMLElement) => {
  const rect = target.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // More accurate tooltip dimension estimates
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let tooltipWidth: number;

  if (ctx) {
    ctx.font = '13px Arial';
    const textWidth = ctx.measureText(text).width;
    // Ensure tooltip fits in viewport - use 90% of viewport width max
    const maxAllowedWidth = Math.min(400, viewportWidth * 0.9);
    tooltipWidth = Math.min(textWidth + 20, maxAllowedWidth); // +20 for padding
  } else {
    // Fallback calculation
    const maxAllowedWidth = Math.min(400, viewportWidth * 0.9);
    tooltipWidth = Math.min(text.length * 7 + 20, maxAllowedWidth);
  }

  const tooltipHeight = 32;
  const arrowHeight = 8;
  const padding = 10;

  let x = rect.left + rect.width / 2;
  let y = rect.top;
  let position = 'top'; // Default position above the element

  // Store the original center position for arrow calculation
  const originalCenterX = rect.left + rect.width / 2;

  // Check if tooltip would go outside viewport horizontally
  if (x - tooltipWidth / 2 < padding) {
    // Too far left, align to left edge with padding
    x = tooltipWidth / 2 + padding;
  } else if (x + tooltipWidth / 2 > viewportWidth - padding) {
    // Too far right, align to right edge with padding
    x = viewportWidth - tooltipWidth / 2 - padding;
  }

  // Calculate arrow offset from tooltip center
  let arrowOffset = originalCenterX - x;

  // Constrain arrow offset to stay within tooltip bounds (leave some padding)
  const maxArrowOffset = tooltipWidth / 2 - 16;
  arrowOffset = Math.max(-maxArrowOffset, Math.min(maxArrowOffset, arrowOffset));

  // Check if tooltip would go outside viewport vertically
  if (y - tooltipHeight - arrowHeight < padding) {
    // Not enough space above, show below the element
    position = 'bottom';
    y = rect.bottom;
  } else if (y + rect.height + tooltipHeight + arrowHeight > viewportHeight - padding) {
    // Not enough space below either, keep above but adjust
    position = 'top';
    y = rect.top;

    // If still not enough space above, force it to fit
    if (y - tooltipHeight - arrowHeight < padding) {
      y = tooltipHeight + arrowHeight + padding;
    }
  }

  if (setTooltip) {
    setTooltip({
      visible: true,
      text,
      x,
      y,
      position: position as 'top' | 'bottom',
      arrowOffset,
    });
  }
};

export const hideTooltip = () => {
  if (setTooltip) {
    setTooltip({ visible: false, text: '', x: 0, y: 0, position: 'top', arrowOffset: 0 });
  }
};

const TooltipManager: React.FC = () => {
  const [tooltip, _setTooltip] = useState<TooltipState>({
    visible: false,
    text: '',
    x: 0,
    y: 0,
    position: 'top',
    arrowOffset: 0,
  });

  useEffect(() => {
    setTooltip = _setTooltip;
  }, []);

  if (!tooltip.visible) return null;

  const isBottom = tooltip.position === 'bottom';

  return (
    <div
      className="global-tooltip"
      style={{
        position: 'fixed',
        top: isBottom ? tooltip.y + 10 : tooltip.y - 40,
        left: tooltip.x,
        zIndex: 9999,
      }}
    >
      {isBottom && (
        <div
          className='global-tooltip__arrow global-tooltip__arrow--bottom'
          style={{
            transform: `translateX(calc(-50% + ${tooltip.arrowOffset || 0}px))`,
          }}
        />
      )}
      <div className='global-tooltip__bubble'>{tooltip.text}</div>
      {!isBottom && (
        <div
          className='global-tooltip__arrow global-tooltip__arrow--top'
          style={{
            transform: `translateX(calc(-50% + ${tooltip.arrowOffset || 0}px))`,
          }}
        />
      )}
    </div>
  );
};

export default TooltipManager;
